import {
  createRootRoute,
  createRoute,
  createRouter,
  notFound,
  Outlet,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useCallback } from "react";
import { type CatalogEntry, catalogEntries, resolveByRoute } from "@/platform/catalog";
import { ARTICLES, resolveArticle } from "@/platform/compendium";
import {
  articleTitle,
  COMPENDIUM_LANDING_TITLE,
  HOMEPAGE_TITLE,
  presetTitle,
} from "@/platform/page-title";
import { ArticlePage } from "@/platform/routes/Article";
import { CompendiumLanding } from "@/platform/routes/CompendiumLanding";
import { CompendiumLayout } from "@/platform/routes/CompendiumLayout";
import { VisualizerPage } from "@/platform/routes/index";
import { useDocumentTitle } from "@/platform/routes/use-document-title";
import { type LensView, parseLensView } from "@/platform/share/link";

// The lens (Keyboard + Keyboard Layout, and compare A/B) rides every route as query view-params,
// never in the path. Validating on the root means every route inherits the schema,
// so a Preset deep-link, /catalog, or / can all carry a shared lens. An absent/garbage query
// decodes to an empty lens, so existing path-only links are unaffected.
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: () => <div style={{ padding: 24 }}>404 — Not Found</div>,
  validateSearch: (search: Record<string, unknown>): LensView => parseLensView(search),
});

// One app surface for every route: the Visualizer, with router-aware navigation injected so the
// shared header pushes real client-side navigation (e.g. into the Compendium).
// Standalone mounts (tests) omit onNavigate; the header's real <a href>s then fall back to a full
// load, which the tests never exercise.
function AppView({ preset }: { preset?: CatalogEntry }) {
  const navigate = useNavigate();
  // keep the tab title in sync: a Preset's setup name, or the bare brand on the homepage
  useDocumentTitle(preset ? presetTitle(preset) : HOMEPAGE_TITLE);
  // the decoded lens from the URL's query (root validateSearch); empty when none was shared
  const lensView = useSearch({ strict: false }) as LensView;
  const onNavigate = useCallback(
    // dynamic Preset paths are not statically-typed routes; navigate by raw path
    (to: string) => navigate({ to } as Parameters<typeof navigate>[0]),
    [navigate],
  );
  return <VisualizerPage preset={preset} onNavigate={onNavigate} lensView={lensView} />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <AppView />,
});

// The Compendium layout: /thecore and its Articles share one chrome - the shared header plus the sub-nav
// tab strip - around an Outlet. This static `thecore` prefix matches ahead of the $ Preset splat, so
// the Compendium is its own root (learn), distinct from /sc2/thecore/... (a setup to view/download).
const compendiumLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "thecore",
  component: CompendiumLayout,
});

// The Compendium landing (the Overview tab): TheCore's home, listing its Articles. Head-only prerendered
// (decision 0014); the crawlable prose lives in the Articles it routes to.
const compendiumIndexRoute = createRoute({
  getParentRoute: () => compendiumLayoutRoute,
  path: "/",
  component: CompendiumLandingRouteView,
});

function CompendiumLandingRouteView() {
  useDocumentTitle(COMPENDIUM_LANDING_TITLE);
  return <CompendiumLanding />;
}

// A Compendium Article at /thecore/<slug>. The loader resolves the Article from the manifest synchronously
// (the HTML is inlined at build), so the first render has its body and the client re-renders over the
// prerendered static page; an unknown slug throws notFound, the same not-found path the splat uses.
const articleRoute = createRoute({
  getParentRoute: () => compendiumLayoutRoute,
  path: "$slug",
  loader: ({ params }) => {
    const article = resolveArticle(ARTICLES, params.slug);
    if (!article) throw notFound();
    return article;
  },
  component: ArticleRouteView,
});

function ArticleRouteView() {
  const article = articleRoute.useLoaderData();
  useDocumentTitle(articleTitle(article));
  return <ArticlePage article={article} />;
}

// Every other path is a candidate Preset route. The splat hands the full path to the catalog
// seam, the single source of truth for what resolves; an unmatched path throws
// notFound, rendering the root notFoundComponent. The matched Preset boots the app preloaded.
const presetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  loader: ({ params }) => {
    const entry = resolveByRoute(catalogEntries, `/${params._splat ?? ""}`);
    if (!entry) throw notFound();
    return entry;
  },
  component: PresetRouteView,
});

function PresetRouteView() {
  const entry = presetRoute.useLoaderData();
  return <AppView preset={entry} />;
}

export const routeTree = rootRoute.addChildren([
  indexRoute,
  compendiumLayoutRoute.addChildren([compendiumIndexRoute, articleRoute]),
  presetRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
